import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useAuthContext} from "./LauncherAuthContext";
import {Button, Card, Flex, message, Modal, Tag, Typography, Row, Col} from "antd";
import {SelectableGame} from "./types";
import {useTranslation} from "react-i18next";
import {LauncherLayout} from "../layout/LauncherLayout";
import {EnsureLoggedIn} from "../../admin/EnsureLoggedIn";
import {launcherCard} from "../common/styles";
import {EditOutlined} from "@ant-design/icons";
import {Label} from "../../commons/translations/Label";
import {useLocation, useSearchParams} from "react-router-dom";
import {useNavigate} from "react-router";
import {GameCollectionDisplay} from "../games/GameCollectionDisplay";
import {translateExperienceValue} from "./components/utils";
import {PlayerProfileCard} from "./components/player-profile-card";
import {NarratorForm} from "./components/NarratorForm";
import {ProfileForm} from "./components/ProfileForm";
import {useQuery} from "react-query";
import { ReservationDTO } from "../../reservation/types";
import { JoinRequestDto } from "../../commons/data/types";
import axios, { AxiosResponse } from "axios";
import { EventCollectionDisplay } from "../events/EventCollectionDisplay";
import { PublicEventDTO } from "../../home/types";
import { BookOutlined, CalendarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

export const ProfilePage: React.FC = () => {

    const auth = useAuthContext();
    const { hasProfile, isNarrator, user: profile } = auth;
    const [editingProfile, setEditingProfile] = useState<boolean>(false);
    const [searchParams] = useSearchParams();
    const [editingNarrator, setEditingNarrator] = useState<boolean>(false);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const hasTarget = useMemo(() => searchParams.has('target'), [searchParams]);
    const redirect = useMemo(() => searchParams.get("target") || location.pathname, [searchParams, location.pathname]);

    useEffect(() => {
        if (!hasProfile()) {
            setEditingProfile(true);
            message.info(t('profile.setup.prompt'))
        } else if (hasTarget) {
            navigate(redirect);
        }
    }, [hasProfile, hasTarget, redirect, t, navigate]);

    const closePlayerProfileModal = useCallback(() => {
        setEditingProfile(false);
    }, []);

    const closeNarratorProfileModal = useCallback(() => {
        setEditingNarrator(false);
    }, []);

    const games = useMemo<SelectableGame[]>(() => profile?.profile?.interests || [], [profile]);
    const narratorGames = useMemo<SelectableGame[]>(() => profile?.narrator?.games || [], [profile]);
    const narratorExperience = useMemo<{ color: string, value: string }>(() => translateExperienceValue(profile?.narrator?.experience), [profile]);

    const { data: activeReservationsEvents, isLoading: isActiveReservationsLoading } = useQuery<PublicEventDTO[]>({
        queryKey: ['my-reservations', profile?.id, false],
        enabled: !!profile?.id,
        queryFn: async () => {
            const response = await axios.get<any, AxiosResponse<ReservationDTO[]>>(`/api/launcher/my/reservations?all=${false}`);
            return (response.data || []).map(r => r.event);
        }
    });

    const { data: activeJoinRequests, isLoading: isJoinRequestsLoading } = useQuery<JoinRequestDto[]>({
        queryKey: ['my-active-join-requests', profile?.id],
        enabled: !!profile?.id,
        queryFn: async () => {
            const response = await axios.get<JoinRequestDto[]>('/api/launcher/my/join-requests');
            return response.data || [];
        }
    });

    const hasNoReservations = (!activeReservationsEvents || activeReservationsEvents.length === 0) &&
        (!activeJoinRequests || activeJoinRequests.length === 0);

    return (
        <LauncherLayout>
            <EnsureLoggedIn>
                <Modal open={editingProfile} closable={false} footer={null}>
                    <ProfileForm closeModal={closePlayerProfileModal} hasTarget redirect={redirect} />
                </Modal>
                <Modal open={editingNarrator} closable={false} footer={null}>
                    <NarratorForm closeModal={closeNarratorProfileModal} />
                </Modal>
                <PlayerProfileCard profile={profile} setEditingProfile={setEditingProfile} />
                <Card
                    style={{ ...launcherCard, marginTop: '5px' }}
                    loading={isActiveReservationsLoading || isJoinRequestsLoading}
                    title={<Typography.Title level={2}><Label trl={'myReservations.title'} /></Typography.Title>}
                >
                    {/* ADVENTURE JOIN REQUESTS */}
                    {activeJoinRequests && activeJoinRequests.length > 0 && (
                        <div style={{ marginBottom: 20 }}>
                            <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 12 }}>
                                <Label trl={'profile.adventuresReservations.title'} />
                            </Typography.Title>
                            <Row gutter={[16, 16]}>
                                {activeJoinRequests.map(jr => (
                                    <Col xs={24} sm={12} md={8} key={jr.id}>
                                        <Card
                                            hoverable
                                            onClick={() => navigate(`/join-requests/${jr.id}`)}
                                            style={{
                                                borderRadius: 8,
                                                border: '1px solid #e8e8e8',
                                                overflow: 'hidden',
                                                cursor: 'pointer',
                                                height: '100%'
                                            }}
                                            bodyStyle={{ padding: 12 }}
                                        >
                                            <Flex gap="small" align="start">
                                                {jr.adventureCoverUrl ? (
                                                    <img
                                                        src={jr.adventureCoverUrl}
                                                        alt={jr.adventureTitle || ''}
                                                        style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 6 }}
                                                    />
                                                ) : (
                                                    <div style={{ width: 70, height: 70, background: '#f0f0f0', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <BookOutlined style={{ fontSize: 22, color: '#aaa' }} />
                                                    </div>
                                                )}
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <Flex justify="space-between" align="center" style={{ marginBottom: 2 }}>
                                                        <Tag color="purple" style={{ margin: 0, fontSize: 10 }}>{t('profile.reservationType.adventure', 'Adventure')}</Tag>
                                                        {jr.status === 'APPROVED' && <Tag color="green" style={{ margin: 0, fontSize: 10 }}>{t('joinRequests.status.approved', 'Confirmed')}</Tag>}
                                                        {jr.status === 'PENDING' && <Tag color="gold" style={{ margin: 0, fontSize: 10 }}>{t('joinRequests.status.pending', 'Pending')}</Tag>}
                                                    </Flex>
                                                    <Typography.Text strong ellipsis style={{ display: 'block', fontSize: 13 }}>
                                                        {jr.adventureTitle}
                                                    </Typography.Text>
                                                    {jr.systemTitle && (
                                                        <Typography.Text type="secondary" ellipsis style={{ display: 'block', fontSize: 11 }}>
                                                            {jr.systemTitle}
                                                        </Typography.Text>
                                                    )}
                                                    {jr.nextSessionTime && (
                                                        <Typography.Text type="secondary" style={{ display: 'block', fontSize: 11, marginTop: 2 }}>
                                                            <CalendarOutlined /> {dayjs(jr.nextSessionTime).format('MMM D, HH:mm')}
                                                        </Typography.Text>
                                                    )}
                                                </div>
                                            </Flex>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    )}

                    {/* TABLE EVENT RESERVATIONS */}
                    {activeReservationsEvents && activeReservationsEvents.length > 0 && (
                        <div>
                            {activeJoinRequests && activeJoinRequests.length > 0 && (
                                <Typography.Title level={4} style={{ marginTop: 12, marginBottom: 12 }}>
                                    <Label trl={'profile.eventsReservations.title'} />
                                </Typography.Title>
                            )}
                            <EventCollectionDisplay events={activeReservationsEvents || []} />
                        </div>
                    )}

                    {hasNoReservations && (
                        <div style={{ textAlign: 'center', padding: '24px 0' }}>
                            <Typography.Paragraph type="secondary">
                                {t('myReservations.none', 'You currently have no active reservations.')}
                            </Typography.Paragraph>
                            <Button type="primary" onClick={() => navigate('/invitations')}>
                                {t('invitations.browse.nav', 'Browse Adventures')}
                            </Button>
                        </div>
                    )}
                </Card>
                {!!profile?.profile?.interestedInGm &&
                    <Card style={{...launcherCard, marginTop: '5px'}} title={<Label trl="profile.narrator.title" />} actions={[<Button icon={<EditOutlined />} onClick={() => setEditingNarrator(true)} size={"large"}><Label trl="common.edit" /></Button>]}>
                        {isNarrator() &&
                            <>
                                <Flex style={{margin: '4px'}} vertical={false} justify={"space-between"} align={"start"}>
                                    <Label trl={'profile.experience'} />
                                    <Tag color={narratorExperience.color} style={{marginLeft: '5px'}}>{narratorExperience.value}</Tag>
                                </Flex>
                                <GameCollectionDisplay games={narratorGames} />
                            </>
                        }
                        {!isNarrator() &&
                            <Button onClick={() => setEditingNarrator(true)} size={"large"}><Label trl="profile.narrator.prompt" /></Button>
                        }
                    </Card>
                }
                <Card style={{...launcherCard, marginTop: '5px'}} title={<Label trl="profile.myGames" />}>
                    <GameCollectionDisplay games={games} />
                </Card>
                <div>
                    {!hasProfile && <Button onClick={() => setEditingProfile(true)} size={"large"}><Label trl="profile.setup.button" /></Button>}
                </div>
            </EnsureLoggedIn>
        </LauncherLayout>
    )
}
